import React, { useEffect, useState } from 'react';
import {
  CreditApplicationColumnType,
  InternalFormSignerSource,
  InternalFormSignerStep,
} from '@dill/dill-shared';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import SignerFieldsEditor, {
  SignerFieldDraft,
  fromSignerFieldDrafts,
  toSignerFieldDrafts,
} from './SignerFieldsEditor';
import { DillButton, DillModal } from '../../../general/Dill';

export const SIGNER_SOURCE_OPTIONS: { label: string; value: InternalFormSignerSource }[] = [
  { label: 'Sales rep', value: 'SALES_REP' },
  { label: 'Branch TM', value: 'BRANCH_TM' },
  { label: 'Region RSM', value: 'REGION_RSM' },
  { label: 'Credit decision makers', value: 'CREDIT_APP_DECISION_MAKER' },
  { label: 'Specific emails', value: 'EMAILS' },
];

export const SIGNER_HOUSE_REP_OPTIONS: {
  label: string;
  value: InternalFormSignerStep['houseRepRequirement'];
}[] = [
  { label: 'Always', value: 'ANY' },
  { label: 'Only when the sales rep is a House Rep', value: 'HOUSE_REP_ONLY' },
  { label: 'Only when the sales rep is not a House Rep', value: 'NON_HOUSE_REP_ONLY' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const splitList = (text: string) =>
  text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

type SignerErrors = { label?: string; emails?: string; values?: string; fields?: string };

// Add or edit one internal form signer (see InternalFormSignersSection). Edits a draft copy — the
// template's signers only change on Save.
const InternalFormSignerModal = ({
  open,
  signer,
  signerNumber,
  onClose,
  onSave,
}: {
  open: boolean;
  // null when adding a new signer.
  signer: InternalFormSignerStep | null;
  // Shown in the title, e.g. "Signer 3" — signer 1 is whoever fills the form in.
  signerNumber: number;
  onClose: () => void;
  onSave: (signer: InternalFormSignerStep) => void;
}) => {
  const [label, setLabel] = useState('');
  const [source, setSource] = useState<InternalFormSignerSource>('BRANCH_TM');
  const [emailsText, setEmailsText] = useState('');
  const [houseRepRequirement, setHouseRepRequirement] =
    useState<InternalFormSignerStep['houseRepRequirement']>('ANY');
  const [salesRepFieldId, setSalesRepFieldId] = useState('');
  const [conditionFieldId, setConditionFieldId] = useState('');
  const [conditionValuesText, setConditionValuesText] = useState('');
  const [fieldDrafts, setFieldDrafts] = useState<SignerFieldDraft[]>([]);
  const [fieldsColumnType, setFieldsColumnType] =
    useState<CreditApplicationColumnType>('TWO_COLUMNS');
  const [errors, setErrors] = useState<SignerErrors>({});

  useEffect(() => {
    if (!open) return;
    setLabel(signer?.label ?? '');
    setSource(signer?.source ?? 'BRANCH_TM');
    setEmailsText((signer?.emails ?? []).join(', '));
    setHouseRepRequirement(signer?.houseRepRequirement ?? 'ANY');
    setSalesRepFieldId(signer?.salesRepFieldId ?? '');
    setConditionFieldId(signer?.condition?.sourceFieldId ?? '');
    setConditionValuesText((signer?.condition?.sourceOptionValues ?? []).join(', '));
    setFieldDrafts(toSignerFieldDrafts(signer?.fields ?? []));
    setFieldsColumnType(signer?.fieldsColumnType ?? 'TWO_COLUMNS');
    setErrors({});
  }, [open, signer]);

  const handleSave = () => {
    const emails = splitList(emailsText);
    const conditionValues = splitList(conditionValuesText);
    const invalidEmail = emails.find((email) => !EMAIL_PATTERN.test(email));
    const signerFields = fromSignerFieldDrafts(fieldDrafts, signer?.fields ?? []);
    const nextErrors: SignerErrors = {
      label: label.trim() ? '' : 'Give this signer a label, e.g. Branch Manager',
      emails:
        source !== 'EMAILS'
          ? ''
          : emails.length === 0
          ? 'Enter at least one email'
          : invalidEmail
          ? `Invalid email: ${invalidEmail}`
          : '',
      values:
        conditionFieldId.trim() && conditionValues.length === 0
          ? 'Enter the answer(s) that make this signer required'
          : '',
      fields: signerFields.error,
    };
    setErrors(nextErrors);
    if (nextErrors.label || nextErrors.emails || nextErrors.values || nextErrors.fields) return;
    onSave({
      ...(signer ?? {}),
      id: signer?.id ?? `signer-${Date.now()}`,
      label: label.trim(),
      source,
      emails: source === 'EMAILS' ? emails : [],
      houseRepRequirement,
      salesRepFieldId: salesRepFieldId.trim(),
      fields: signerFields.fields,
      fieldsColumnType,
      condition: conditionFieldId.trim()
        ? { sourceFieldId: conditionFieldId.trim(), sourceOptionValues: conditionValues }
        : null,
    });
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title={`${signer ? 'Edit' : 'Add'} Signer ${signerNumber}`}
      width="520px"
      maxWidth="520px"
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton label="Cancel" variant="secondary" onClick={onClose} />
          <DillButton label={signer ? 'Save' : 'Add Signer'} onClick={handleSave} />
        </div>
      }>
      <div className="flex flex-col gap-3">
        <AppInputField
          label="Label"
          isRequired
          placeholder="e.g. Branch Manager"
          value={label}
          errorText={errors.label || ''}
          onTextChange={(text: string) => {
            setLabel(text);
            setErrors((prev) => ({ ...prev, label: '' }));
          }}
        />
        <AppDropDown
          label="Who signs"
          value={source}
          items={SIGNER_SOURCE_OPTIONS}
          onSelectChange={(item) => {
            if (!item?.value) return;
            setSource(item.value as InternalFormSignerSource);
            setErrors((prev) => ({ ...prev, emails: '' }));
          }}
        />
        {source === 'EMAILS' && (
          <AppInputField
            label="Emails (comma-separated, any one of them signs)"
            isRequired
            placeholder="e.g. controller@company.com"
            value={emailsText}
            errorText={errors.emails || ''}
            onTextChange={(text: string) => {
              setEmailsText(text);
              setErrors((prev) => ({ ...prev, emails: '' }));
            }}
          />
        )}
        <SignerFieldsEditor
          drafts={fieldDrafts}
          columnType={fieldsColumnType}
          onColumnTypeChange={setFieldsColumnType}
          error={errors.fields || ''}
          onChange={(drafts) => {
            setFieldDrafts(drafts);
            setErrors((prev) => ({ ...prev, fields: '' }));
          }}
        />
        <AppDropDown
          label="Sales rep condition"
          value={houseRepRequirement}
          items={SIGNER_HOUSE_REP_OPTIONS}
          onSelectChange={(item) => {
            if (item?.value) {
              setHouseRepRequirement(item.value as InternalFormSignerStep['houseRepRequirement']);
            }
          }}
        />
        {(source === 'SALES_REP' || houseRepRequirement !== 'ANY') && (
          <AppInputField
            label="Sales rep field ID (optional)"
            placeholder="supplier-sales-reps"
            value={salesRepFieldId}
            onTextChange={(text: string) => setSalesRepFieldId(text)}
          />
        )}
        <div className="border border-gray-100 p-3 rounded-lg flex flex-col gap-2">
          <p className="text-xs font-medium text-gray-500">Only required if… (optional)</p>
          <AppInputField
            label="Field ID"
            placeholder="e.g. include-name-change-or-new-choice"
            value={conditionFieldId}
            onTextChange={(text: string) => {
              setConditionFieldId(text);
              setErrors((prev) => ({ ...prev, values: '' }));
            }}
          />
          <AppInputField
            label="Values (comma-separated)"
            placeholder="e.g. Yes"
            value={conditionValuesText}
            errorText={errors.values || ''}
            onTextChange={(text: string) => {
              setConditionValuesText(text);
              setErrors((prev) => ({ ...prev, values: '' }));
            }}
          />
        </div>
      </div>
    </DillModal>
  );
};

export default InternalFormSignerModal;
